import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FooterButton } from '../../core/components';
import { OverheadCraneService } from '../../service/api/overhead-crane-service';
import { showToast } from '../../core/redux/toast/slice';
import { useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import { Trash2 } from 'react-feather';

const initData = {
  id: 0,
  date: new Date().toISOString(),
  itemNumber: 0,
  question: '',
  status: null,
  comments: '',
};

const ignoreKeys = ['comments', 'itemNumber', 'date', 'id'];

const SafetyChecklist = ({ overheadCraneId, onCloseModal, safetyActionRecord }) => {
  const dispatch = useDispatch();
  const [safetyCheckItems, setSafetyCheckItems] = useState([
    { ...initData, overheadCraneId: overheadCraneId }
  ]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSafetyChecklist();
  }, [overheadCraneId]);

  const fetchSafetyChecklist = async () => {
    try {
      setLoading(true);
      const response = await OverheadCraneService.getOverheadCraneSafetyChecklistByOverheadCrane(overheadCraneId);
      if (response.status === 200 && response.response?.length > 0) {
        setSafetyCheckItems(response.response);
      } else {
        setSafetyCheckItems([{ ...initData, overheadCraneId: overheadCraneId }]);
      }
    } catch (error) {
      console.error('Error fetching safety checklist:', error);
      dispatch(showToast({ message: 'Error loading safety checklist', type: 'danger' }));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (index, field, value) => {
    const updated = [...safetyCheckItems];
    const updatedItem = { ...updated[index] };
    updatedItem[field] = value;
    updatedItem['isDirty'] = true;
    updated[index] = updatedItem;
    setSafetyCheckItems(updated);
  };

  const addSafetyCheckItem = () => {
    const newItem = {
      ...initData,
      overheadCraneId: overheadCraneId,
      date: new Date().toISOString(),
    };
    setSafetyCheckItems([...safetyCheckItems, newItem]);
  };

  const deleteSafetyCheckItem = async (id, index) => {
    if (id === 0) {
      setSafetyCheckItems(items => items.filter((_, i) => i !== index));
      return;
    }

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    });

    if (result.isConfirmed) {
      try {
        const response = await OverheadCraneService.deleteOverheadCraneSafetyChecklist(id);
        if (response.status === 200) {
          setSafetyCheckItems(items => items.filter(item => item.id !== id));
          dispatch(showToast({ message: 'Item deleted successfully', type: 'success' }));
        }
      } catch (error) {
        dispatch(showToast({ message: 'Failed to delete. Try again!', type: 'danger' }));
      }
    }
  };

  const valid = payload => {
    if (
      !payload ||
      Object.keys(payload).some(key => {
        return (
          !ignoreKeys.includes(key) &&
          (payload[key] === undefined || payload[key] === null || payload[key] === '')
        );
      })
    ) {
      dispatch(
        showToast({
          message: 'Please fill all required fields',
          type: 'danger',
        })
      );
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    const dirtyItems = safetyCheckItems.filter(item => item.isDirty || item.id === 0);
    
    if (dirtyItems.length === 0) {
      dispatch(showToast({ message: 'No changes to save', type: 'info' }));
      return;
    }

    for (const checkListItem of dirtyItems) {
      const payload = {
        id: checkListItem.id,
        question: checkListItem.question,
        status: checkListItem.status,
        comments: checkListItem.comments,
        overheadCraneId: overheadCraneId,
        date: checkListItem.date || new Date().toISOString(),
        userId: localStorage.getItem('UserId'),
      };

      if (valid(payload)) {
        try {
          const response = await OverheadCraneService.createOverheadCraneSafetyChecklist(payload);
          if (response.status === 200) {
            dispatch(showToast({ message: 'Safety checklist saved successfully', type: 'success' }));
          }
        } catch (error) {
          dispatch(showToast({ message: 'Failed to save. Try again!', type: 'danger' }));
        }
      }
    }
    
    await fetchSafetyChecklist();
  };

  const handleCancel = () => {
    onCloseModal();
  };

  return (
    <>
      <div className="mb-3">
        <button className="btn btn-info me-2" onClick={addSafetyCheckItem}>
          + Add Safety Check Item
        </button>
      </div>
      <div>
        {safetyCheckItems.map((item, index) => (
          <div className="row border" key={index}>
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-3 mt-3">Safety Check Item #{index + 1}</h5>
              <button
                className="btn btn-danger"
                onClick={() => deleteSafetyCheckItem(item.id, index)}
              >
                <Trash2 size={16} />
              </button>
            </div>

            <div className="col-lg-6 col-md-6 col-sm-12">
              <div className="input-blocks">
                <label>
                  Task/Question <span className="text-danger">*</span>
                </label>
                <textarea
                  rows={5}
                  cols={5}
                  className="form-control"
                  placeholder="Enter the safety check task or question"
                  value={item.question || ''}
                  onChange={e => handleInputChange(index, 'question', e.target.value)}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-lg-6 col-md-6 col-sm-12">
                <div className="input-blocks">
                  <label>
                    Status <span className="text-danger">*</span>
                  </label>
                  <div className="btn-group" role="group" aria-label="Basic radio toggle button group">
                    <input
                      type="radio"
                      className="btn-check"
                      id={`btnradioYes${index}`}
                      name={`btnradioStatus${index}`}
                      checked={item.status === true}
                      onChange={e => handleInputChange(index, 'status', true)}
                    />
                    <label className="btn btn-outline-primary" htmlFor={`btnradioYes${index}`}>
                      Yes
                    </label>
                    <input
                      type="radio"
                      className="btn-check"
                      id={`btnradioNo${index}`}
                      name={`btnradioStatus${index}`}
                      checked={item.status === false}
                      onChange={e => handleInputChange(index, 'status', false)}
                    />
                    <label className="btn btn-outline-primary" htmlFor={`btnradioNo${index}`}>
                      No
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-12 col-md-12 col-sm-12">
              <div className="input-blocks">
                <label>
                  Comments <span className="text-danger">*</span>
                </label>
                <textarea
                  rows={5}
                  cols={5}
                  className="form-control"
                  placeholder="Enter any comments or observations"
                  value={item.comments || ''}
                  onChange={e => handleInputChange(index, 'comments', e.target.value)}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row mt-2">
        <div className="col-12 col-lg-8"></div>
        <div className="col-12 col-lg-4">
          <FooterButton
            saveText="Save Safety Checklist"
            onSave={handleSave}
            onCancel={handleCancel}
          />
        </div>
      </div>
    </>
  );
};

SafetyChecklist.propTypes = {
  overheadCraneId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCloseModal: PropTypes.func,
  safetyActionRecord: PropTypes.object,
};

export default SafetyChecklist;
